import React, { useEffect, useRef, useState } from 'react';

import { GrowthBook, GrowthBookProvider, useFeatureValue } from '@growthbook/growthbook-react';
import { analytics } from '@features/analytics';
import { useSelector } from 'react-redux';
import { basketSelectors } from '@store/basket/selectors';
import Cookies from 'js-cookie';
import { shareGrowthBook } from './protectionExperimentGroup';

import {
  applyFeatureOverrides,
  collectFeatureOverrides,
  EARLY_ADOPTER_FLAG,
  FeatureOverrides,
  readAllowedFlags,
} from './earlyAdopterOverrides';

type FeatureFlagProviderProps = {
  children: React.ReactNode;
  clientKey: string;
  siteSlug: string;
};

type EarlyAdopterSyncProps = {
  gb: GrowthBook;
  overrides: FeatureOverrides;
};

function EarlyAdopterSync({ gb, overrides }: EarlyAdopterSyncProps) {
  const allowedFlags = readAllowedFlags(useFeatureValue<string[]>(EARLY_ADOPTER_FLAG, []));
  const allowedKey = allowedFlags.join(',');
  const appliedKey = useRef(allowedKey);

  useEffect(() => {
    if (appliedKey.current === allowedKey) return;

    appliedKey.current = allowedKey;
    applyFeatureOverrides(gb, overrides, allowedKey ? allowedKey.split(',') : []);
  }, [gb, overrides, allowedKey]);

  return null;
}

export function FeatureFlagProvider({ children, clientKey, siteSlug }: FeatureFlagProviderProps) {
  const orderReference = useSelector((state) => basketSelectors.getOrder(state)?.orderReference);
  const appliedOrderReference = useRef<string | undefined>(undefined);

  const [gb] = useState(() => {
    return new GrowthBook({
      apiHost: 'https://growthbook-proxy.control.onejourney.travel',
      clientKey,
      attributes: { id: Cookies.get('gbuuid'), siteSlug },
      trackingCallback: (experiment, result) => {
        analytics.track({
          event: 'experiment_viewed',
          params: {
            experiment_id: experiment.key,
            variation_id: result.variationId,
            site_slug: siteSlug,
          },
        });
      },
    });
  });

  const [ready, setReady] = useState(false);
  const [overrides, setOverrides] = useState<FeatureOverrides>({});

  useEffect(() => {
    shareGrowthBook(gb);
    gb.init({ streaming: true, timeout: 2000 })
      .catch(() => undefined)
      .then(() => {
        const collected = collectFeatureOverrides(window.location.search);
        applyFeatureOverrides(
          gb,
          collected,
          readAllowedFlags(gb.getFeatureValue<string[]>(EARLY_ADOPTER_FLAG, []))
        );
        setOverrides(collected);
        setReady(true);
      });
  }, [gb]);

  useEffect(() => {
    if (!ready) return;
    if (appliedOrderReference.current === orderReference) return;

    appliedOrderReference.current = orderReference;
    gb.updateAttributes({ orderReference });
  }, [gb, ready, orderReference]);

  if (!ready) return null;

  return (
    <GrowthBookProvider growthbook={gb}>
      <EarlyAdopterSync gb={gb} overrides={overrides} />
      {children}
    </GrowthBookProvider>
  );
}
